/** Auxiliary info sync — `/v2/console/ops/aux_sync/*` (perm console_bg_aux_sync). */
import { apiGet, apiPost } from './client';

export interface AuxSyncAccount {
  slug: string | null;
  display_name: string;
  is_default: boolean;
}

export interface AuxSyncIntegration {
  integration: string;
  accounts: AuxSyncAccount[];
  features: string[];
  /** full = dedicated collector (API probes + payload analyzers); generic = payload analyzers only. */
  supported: 'full' | 'generic';
}

export interface AuxSyncLaunchContext {
  tour_operator_id: number;
  integrations: AuxSyncIntegration[];
  defaults: { from_date: string; to_date: string; sample_limit: number };
}

export interface AuxSyncStartInput {
  tour_operator_id: number;
  integration_name: string;
  accounts?: (string | null)[] | null;
  from_date?: string | null;
  to_date?: string | null;
  sample_limit?: number | null;
  use_ai?: boolean;
  ai_budget_usd?: number | null;
  fetch_product_content?: boolean;
}

export interface AuxKindColumn {
  field: string;
  label: string;
  type: string;
}

export interface AuxKindSpec {
  code: string;
  label: string;
  target: 'tag' | 'config' | 'info';
  section: 'rosters' | 'config' | 'mapping' | 'info';
  restechs: string[];
  columns: AuxKindColumn[];
  /** JSON Schema over `data`; null = read-only kind. */
  edit_schema: Record<string, unknown> | null;
  addable: boolean;
  options: string[];
  selection_requires: string[];
  category_default: string | null;
  count: number;
  counts_by_status: Record<string, number>;
}

export type AuxRecordStatus = 'new' | 'existing' | 'changed';

export interface AuxRecordSummary {
  key: string;
  kind: string;
  restech: string;
  account: string | null;
  source: 'api' | 'payload' | 'ai' | 'manual';
  status: AuxRecordStatus;
  data: Record<string, unknown>;
  diff_count: number;
  confidence: number | null;
  issues: string[];
  selected_default: boolean;
  options: Record<string, Record<string, unknown>>;
  missing_for_selection: string[];
}

export interface AuxRecordDetail extends AuxRecordSummary {
  target: string;
  evidence: string[];
  proposed: Record<string, unknown>;
  current: Record<string, unknown> | null;
  diff: { field: string; before: unknown; after: unknown }[];
}

export interface AuxSyncSummary {
  total: number;
  by_kind: Record<string, number>;
  by_status: Record<string, number>;
  by_target: Record<string, number>;
  warnings_count: number;
  ai_cost_usd: number;
  partial: boolean;
  partial_reason: string | null;
  sampling: {
    integration?: string;
    accounts?: string[];
    from_date?: string | null;
    to_date?: string | null;
    bookings_sampled?: Record<string, number>;
    payloads_analyzed?: Record<string, number>;
    probes?: { name: string; ok: boolean; n?: number; error?: string }[];
  };
}

export interface AuxRecordsPage {
  task_id: string;
  task_state: number;
  integration: string | null;
  summary: AuxSyncSummary;
  kinds: AuxKindSpec[];
  count: number;
  page: number;
  page_size: number;
  results: AuxRecordSummary[];
}

export interface AuxApplyReceipt {
  key: string;
  outcome: 'created' | 'restored' | 'updated' | 'unchanged' | 'skipped' | 'error';
  detail?: string;
  before?: unknown;
  after?: unknown;
  tag_id?: number | null;
  user_outcome?: string;
}

export interface AuxResultPage {
  task_id: string;
  rollup: Record<string, unknown>;
  count: number;
  page: number;
  page_size: number;
  results: AuxApplyReceipt[];
}

export function launchContext(tourOperatorId: number): Promise<AuxSyncLaunchContext> {
  return apiGet(`/ops/aux_sync/launch_context/?tour_operator_id=${tourOperatorId}`);
}

export function start(input: AuxSyncStartInput): Promise<{ id: string; state: number }> {
  return apiPost('/ops/aux_sync/start/', input);
}

export interface ListRecordsParams {
  page?: number;
  page_size?: number;
  kind?: string;
  section?: string;
  /** CSV of new/existing/changed. */
  status?: string;
  search?: string;
}

export function listRecords(taskId: string, params: ListRecordsParams): Promise<AuxRecordsPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  return apiGet(`/ops/aux_sync/${taskId}/records/?${qs.toString()}`);
}

export function recordDetail(
  taskId: string,
  key: string,
): Promise<{ task_id: string; record: AuxRecordDetail }> {
  return apiGet(`/ops/aux_sync/${taskId}/record/?key=${encodeURIComponent(key)}`);
}

export function applyResult(
  taskId: string,
  params: { page?: number; page_size?: number; outcome?: string } = {},
): Promise<AuxResultPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  return apiGet(`/ops/aux_sync/${taskId}/result/?${qs.toString()}`);
}
